import React, { useEffect, useRef, useState } from "react";
import {
  FaChevronDown as ChevronDown,
  FaCopy as Copy,
  FaCheck as Check,
  FaDownload as Download,
  FaFileArchive as Archive,
  FaFilePdf as Pdf,
  FaTicketAlt as Ticket,
  FaTimes as CloseIcon,
} from "react-icons/fa";
import { downloadVouchersCodesPdf, downloadVouchersCodesZip } from "../../utils";
import { CreateCreditVouchers } from "../../types";
import { VoucherQrCard } from "../voucher-qr-card";
import { generateVoucherDeeplinkUrl } from "src/utils/util";

type CreatedVouchersProps = {
  codes: string[];
  vouchersInfo: CreateCreditVouchers & { organizationName?: string; projectName?: string };
};

const VOUCHER_LINK_TEXT = "Apply voucher code";

const CreatedVouchersModal: React.FC<CreatedVouchersProps> = ({ codes, vouchersInfo }) => {
  const [isOpened, setIsOpened] = useState(true);
  const [copiedType, setCopiedType] = useState<"codes" | "links" | "both" | null>(null);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [downloadType, setDownloadType] = useState<"pdf" | "zip" | null>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeDownloadMenu = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setIsDownloadMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeDownloadMenu);
    return () => document.removeEventListener("mousedown", closeDownloadMenu);
  }, []);

  if (!isOpened) return null;

  const writeClipboard = async (plainText: string, htmlText?: string) => {
    if (htmlText && navigator.clipboard.write && window.ClipboardItem) {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([htmlText], { type: "text/html" }),
          "text/plain": new Blob([plainText], { type: "text/plain" }),
        }),
      ]);
      return;
    }

    await navigator.clipboard.writeText(plainText);
  };

  const copyToClipboard = async (type: "codes" | "links" | "both") => {
    const vouchers = codes.map((code) => ({
      code,
      link: generateVoucherDeeplinkUrl(code),
    }));
    const markdownLink = (link: string) => `[${VOUCHER_LINK_TEXT}](${link})`;
    const htmlLink = (link: string) => `<a href="${link}">${VOUCHER_LINK_TEXT}</a>`;
    const plainTextByType = {
      codes: codes.join("\n"),
      links: vouchers.map(({ link }) => markdownLink(link)).join("\n"),
      both: vouchers.map(({ code, link }) => `${code}\n${markdownLink(link)}`).join("\n\n"),
    };
    const htmlTextByType = {
      codes: "",
      links: vouchers.map(({ link }) => htmlLink(link)).join("<br />"),
      both: vouchers.map(({ code, link }) => `<p>${code}<br />${htmlLink(link)}</p>`).join(""),
    };

    await writeClipboard(plainTextByType[type], htmlTextByType[type]);
    setCopiedType(type);
    setTimeout(() => setCopiedType(null), 2000);
  };

  const handleDownload = async (type: "pdf" | "zip") => {
    setIsDownloadMenuOpen(false);
    setDownloadType(type);

    try {
      if (type === "zip") {
        await downloadVouchersCodesZip({ codes, vouchersInfo });
      } else {
        await downloadVouchersCodesPdf({ codes, vouchersInfo });
      }
    } finally {
      setDownloadType(null);
    }
  };

  const listContainerClasses = "min-h-0 overflow-y-auto pr-2";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-950 bg-opacity-60 p-4 backdrop-blur-sm dark:bg-black dark:bg-opacity-70">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 dark:bg-gray-900 dark:ring-white dark:ring-opacity-10">
        {/* header */}

        <div className="flex flex-col gap-4 border-b border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm dark:bg-indigo-500">
              <Ticket size={22} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-950 dark:text-white">{codes.length} Vouchers Created</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Download, copy, or share redemption links</p>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div ref={downloadMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setIsDownloadMenuOpen((isOpen) => !isOpen)}
                disabled={downloadType !== null}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus:outline-none disabled:cursor-wait disabled:opacity-70 dark:bg-indigo-500 dark:hover:bg-indigo-600 sm:w-auto"
                aria-haspopup="menu"
                aria-expanded={isDownloadMenuOpen}
              >
                <Download size={14} />
                {downloadType ? "Preparing..." : "PDF"}
                <ChevronDown size={10} className={`transition-transform ${isDownloadMenuOpen ? "rotate-180" : ""}`} />
              </button>

              {isDownloadMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-20 mt-2 w-52 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleDownload("pdf")}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
                  >
                    <Pdf size={15} className="text-red-500" />
                    Combined PDF
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleDownload("zip")}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
                  >
                    <Archive size={15} className="text-indigo-500" />
                    Separate PDFs (.zip)
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-1 rounded-lg border border-blue-200 bg-blue-50 p-1 shadow-sm dark:border-blue-800 dark:bg-blue-950">
              {[
                { type: "codes" as const, label: "Codes" },
                { type: "links" as const, label: "Links" },
                { type: "both" as const, label: "Both" },
              ].map((option) => {
                const isCopied = copiedType === option.type;

                return (
                  <button
                    key={option.type}
                    onClick={() => copyToClipboard(option.type)}
                    className={`flex items-center justify-center gap-1 rounded-md border px-3.5 py-2 text-sm font-semibold shadow-sm transition-colors ${
                      isCopied
                        ? "border-emerald-500 bg-emerald-600 text-white dark:border-emerald-400 dark:bg-emerald-500 dark:text-white"
                        : "border-blue-600 bg-blue-600 text-white hover:border-blue-700 hover:bg-blue-700 dark:border-blue-400 dark:bg-blue-500 dark:text-white dark:hover:border-blue-300 dark:hover:bg-blue-400"
                    }`}
                  >
                    {isCopied ? <Check size={14} /> : <Copy size={14} />}
                    {isCopied ? "Copied" : option.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setIsOpened(false)}
              className="hidden h-10 w-10 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-100 md:flex"
              aria-label="Close"
              title="Close"
            >
              <CloseIcon size={16} />
            </button>
          </div>
        </div>

        {/* scrollable list */}
        <div className={`${listContainerClasses} bg-white p-4 dark:bg-gray-900`}>
          <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2">
            {codes.map((code, index) => (
              <li key={code} className="list-none rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:shadow-none dark:hover:border-gray-600">
                <div className="mb-3 min-w-0 rounded-lg border border-gray-100 bg-gray-50 p-2.5 dark:border-gray-700 dark:bg-gray-900">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Voucher Code</p>
                    <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 dark:bg-indigo-900 dark:text-indigo-100">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <p className="mt-2 truncate font-mono text-sm font-semibold text-gray-950 dark:text-white" title={code}>
                    {code}
                  </p>
                </div>
                <VoucherQrCard code={code} title="Scan" size={96} className="border-gray-100 shadow-sm dark:border-gray-700 dark:shadow-none" />
              </li>
            ))}
          </ul>
        </div>

        {/* footer */}
        <div className="border-t border-gray-200 bg-white p-5 text-right dark:border-gray-700 dark:bg-gray-800">
          <button
            onClick={() => setIsOpened(false)}
            className="rounded-lg border border-gray-300 bg-gray-100 px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-200 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatedVouchersModal;
